import { useMemo } from "react";
import { Check, MapPin, Minus, Plus, ShieldCheck, Star } from "lucide-react";
import { eur } from "@/lib/format";

/**
 * "Mejora tu viaje" — cross-sell picker for the pre-checkin wizard.
 *
 * products: catalog from GET /api/catalog (active, sorted)
 * days: rental days (for per_day price display)
 * value: { [product_id]: qty }
 * onChange(next)
 *
 * Groups:
 *  - exclusive_group === "insurance" → radio cards with coverage bullets
 *  - any other exclusive_group       → radio cards (km, recogida, devolución…)
 *  - no group                        → cards with image + qty stepper
 *
 * Cualquier grupo exclusivo se pinta como radio: el backend ya rechaza dos
 * opciones del mismo grupo (`compute_selection`), así que la UI tiene que
 * impedirlo en vez de dejar que el cliente descubra el error al guardar.
 */

function itemPrice(product, days, qty = 1) {
  const price = Number(product.price || 0);
  if (product.price_type === "per_day") return price * days * qty;
  return price * qty; // per_rental | per_unit
}

function priceHint(product) {
  const price = Number(product.price || 0);
  if (price === 0) return "Incluido";
  if (product.price_type === "per_day") return `${price.toFixed(0)} €/día`;
  if (product.price_type === "per_unit") return `${price.toFixed(0)} €/unidad`;
  return `${price.toFixed(0)} €/viaje`;
}

function RadioGroup({ title, icon, products, days, value, onChange, testPrefix }) {
  const selectedId = products.find((p) => value[p.id])?.id || null;
  return (
    <div>
      <div className="text-overline mb-2 flex items-center gap-2">{icon}{title}</div>
      <div className="space-y-2">
        {products.map((p) => {
          const selected = selectedId === p.id;
          const total = itemPrice(p, days);
          return (
            <label
              key={p.id}
              className={`block border-2 rounded-xl p-4 cursor-pointer transition-all ${
                selected ? "border-marca bg-marca/5" : "border-[#DCD8D0] bg-white"
              }`}
              data-testid={`${testPrefix}-${p.sku}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <input
                    type="radio"
                    checked={selected}
                    onChange={() => {
                      const next = { ...value };
                      products.forEach((other) => delete next[other.id]);
                      next[p.id] = 1;
                      onChange(next);
                    }}
                    className="accent-marca mt-1"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A1A1A] flex items-center gap-2 flex-wrap">
                      {p.name}
                      {p.meta?.recommended && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E89B33]/15 text-[#9C6512] px-2 py-0.5 rounded-full">
                          <Star className="h-3 w-3" /> Recomendado
                        </span>
                      )}
                    </div>
                    {p.short_description && (
                      <div className="text-xs text-[#8A8A8A] mt-0.5">{p.short_description}</div>
                    )}
                    {(p.coverage_details || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {p.coverage_details.map((c, i) => (
                          <li key={i} className="text-xs text-[#5C5C5C] flex items-start gap-1.5">
                            <Check className="h-3.5 w-3.5 text-[#4A7C59] shrink-0 mt-0.5" /> {c}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-marca">
                    {total === 0 ? "Incluido" : eur(total)}
                  </div>
                  <div className="text-[10px] text-[#8A8A8A]">{priceHint(p)}</div>
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function CatalogPicker({ products, days, value, onChange }) {
  const GROUP_TITLES = {
    km: "Paquete de kilómetros",
    recogida: "¿Dónde recoges la camper?",
    devolucion: "¿Dónde la devuelves?",
  };

  const groups = useMemo(() => {
    const insurance = products.filter((p) => p.exclusive_group === "insurance");
    // Orden estable: el que trae el catálogo (sort_order), no el del objeto.
    const exclusive = [];
    for (const p of products) {
      const g = p.exclusive_group;
      if (!g || g === "insurance") continue;
      let bucket = exclusive.find((b) => b.key === g);
      if (!bucket) exclusive.push((bucket = { key: g, items: [] }));
      bucket.items.push(p);
    }
    const other = products.filter((p) => !p.exclusive_group);
    return { insurance, exclusive, other };
  }, [products]);

  const total = useMemo(
    () =>
      products.reduce((sum, p) => {
        const qty = value[p.id] || 0;
        return sum + (qty > 0 ? itemPrice(p, days, p.exclusive_group ? 1 : qty) : 0);
      }, 0),
    [products, days, value]
  );

  const setQty = (p, qty) => {
    const next = { ...value };
    const maxQty = Number(p.max_qty || 1);
    const clamped = Math.max(0, Math.min(maxQty, qty));
    if (clamped === 0) delete next[p.id];
    else next[p.id] = clamped;
    onChange(next);
  };

  return (
    <div className="space-y-6" data-testid="catalog-picker">
      {groups.insurance.length > 0 && (
        <RadioGroup
          title="Protege tu viaje"
          icon={<ShieldCheck className="h-4 w-4 text-marca" />}
          products={groups.insurance}
          days={days}
          value={value}
          onChange={onChange}
          testPrefix="cat-ins"
        />
      )}

      {groups.exclusive.map((g) => (
        <RadioGroup
          key={g.key}
          title={GROUP_TITLES[g.key] || g.key}
          icon={g.key === "recogida" || g.key === "devolucion" ? <MapPin className="h-4 w-4 text-marca" /> : null}
          products={g.items}
          days={days}
          value={value}
          onChange={onChange}
          testPrefix={`cat-${g.key}`}
        />
      ))}

      {groups.other.length > 0 && (
        <div>
          <div className="text-overline mb-2">Extras para tu aventura</div>
          <div className="grid grid-cols-2 gap-3">
            {groups.other.map((p) => {
              const qty = value[p.id] || 0;
              const maxQty = Number(p.max_qty || 1);
              const unit = itemPrice(p, days);
              return (
                <div
                  key={p.id}
                  className={`border-2 rounded-xl overflow-hidden bg-white transition-all ${
                    qty > 0 ? "border-marca" : "border-[#DCD8D0]"
                  }`}
                  data-testid={`cat-extra-${p.sku}`}
                >
                  {p.image_url ? (
                    <div className="aspect-[4/3] bg-[#F7F5F0] overflow-hidden">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-[#F7F5F0] flex items-center justify-center text-3xl">
                      🏕️
                    </div>
                  )}
                  <div className="p-3">
                    <div className="font-medium text-sm text-[#1A1A1A] leading-tight">{p.name}</div>
                    {p.short_description && (
                      <div className="text-[11px] text-[#8A8A8A] mt-0.5 leading-snug">{p.short_description}</div>
                    )}
                    <div className="flex items-center justify-between mt-2.5">
                      <div>
                        <div className="text-sm font-semibold text-marca">
                          {unit === 0 ? "Incluido" : eur(unit)}
                        </div>
                        {unit > 0 && <div className="text-[10px] text-[#8A8A8A]">{priceHint(p)}</div>}
                      </div>
                      {qty === 0 ? (
                        <button
                          type="button"
                          onClick={() => setQty(p, 1)}
                          className="text-xs font-semibold bg-marca text-white rounded-lg px-3 py-1.5 hover:bg-marca-hover"
                          data-testid={`cat-add-${p.sku}`}
                        >
                          Añadir
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setQty(p, qty - 1)}
                            className="h-7 w-7 rounded-lg border border-[#DCD8D0] flex items-center justify-center hover:border-marca"
                            data-testid={`cat-minus-${p.sku}`}
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="text-sm font-semibold w-5 text-center">{qty}</span>
                          <button
                            type="button"
                            onClick={() => setQty(p, qty + 1)}
                            disabled={qty >= maxQty}
                            className="h-7 w-7 rounded-lg border border-[#DCD8D0] flex items-center justify-center hover:border-marca disabled:opacity-40"
                            data-testid={`cat-plus-${p.sku}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="sticky bottom-0 bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-4 flex items-center justify-between shadow-sm">
        <div>
          <div className="text-overline">Total mejoras ({days} día{days !== 1 ? "s" : ""})</div>
          <div className="text-xs text-[#8A8A8A]">Se cobran junto a la fianza, en un único pago</div>
        </div>
        <div className="font-display text-2xl font-bold text-marca" data-testid="cat-total">
          {eur(total)}
        </div>
      </div>
    </div>
  );
}
